/** 核价单自动同意/拒绝规则（按店铺存 preferences） */

import { latestCostHistory } from '@/temu-platform/modules/discuss/discussDisplay';

export const DISCUSS_AUTO_RULES_PREF_KEY = 'discussAutoRulesByShop';
export const DISCUSS_AUTO_RULE_MODE_RATIO = 'ratio';
export const DISCUSS_AUTO_RULE_MODE_PRICE = 'price';

export function makeDiscussAutoRuleId() {
  return `dar_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function clampPercent(n) {
  return Math.min(100, Math.max(0, n));
}

function clampPriceYuan(n) {
  return Math.max(0, n);
}

/** 平台金额多为分；已是小数则视为元 */
export function discussAmountToYuan(amount) {
  if (amount === undefined || amount === null || amount === '') return null;
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  if (Number.isInteger(n) && Math.abs(n) >= 1) return n / 100;
  return n;
}

export function normalizeDiscussAutoRule(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const categoryId = String(raw.categoryId ?? '').trim();
  if (!categoryId) return null;
  const mode = String(raw.mode || '').trim() === DISCUSS_AUTO_RULE_MODE_PRICE
    ? DISCUSS_AUTO_RULE_MODE_PRICE
    : DISCUSS_AUTO_RULE_MODE_RATIO;

  if (mode === DISCUSS_AUTO_RULE_MODE_PRICE) {
    const rejectBelowPrice = Number(raw.rejectBelowPrice);
    const acceptAbovePrice = Number(raw.acceptAbovePrice);
    if (!Number.isFinite(rejectBelowPrice) || !Number.isFinite(acceptAbovePrice)) return null;
    if (rejectBelowPrice < 0 || acceptAbovePrice < 0) return null;
    if (rejectBelowPrice >= acceptAbovePrice) return null;
    return {
      id: String(raw.id || makeDiscussAutoRuleId()),
      enabled: raw.enabled !== false,
      mode,
      categoryId,
      categoryPathLabel: String(raw.categoryPathLabel || '').trim(),
      productTypeId: raw.productTypeId != null ? String(raw.productTypeId) : '',
      rejectBelowPrice: clampPriceYuan(rejectBelowPrice),
      acceptAbovePrice: clampPriceYuan(acceptAbovePrice),
      rejectBelowPercent: 0,
      acceptAbovePercent: 0,
    };
  }

  const rejectBelowPercent = Number(raw.rejectBelowPercent);
  const acceptAbovePercent = Number(raw.acceptAbovePercent);
  if (!Number.isFinite(rejectBelowPercent) || !Number.isFinite(acceptAbovePercent)) return null;
  if (rejectBelowPercent >= acceptAbovePercent) return null;
  return {
    id: String(raw.id || makeDiscussAutoRuleId()),
    enabled: raw.enabled !== false,
    mode,
    categoryId,
    categoryPathLabel: String(raw.categoryPathLabel || '').trim(),
    productTypeId: raw.productTypeId != null ? String(raw.productTypeId) : '',
    rejectBelowPercent: clampPercent(rejectBelowPercent),
    acceptAbovePercent: clampPercent(acceptAbovePercent),
    rejectBelowPrice: 0,
    acceptAbovePrice: 0,
  };
}

export function readDiscussAutoRulesFromPreferences(preferences, shopId) {
  const sid = String(shopId || '').trim();
  if (!sid) return [];
  const root = preferences && typeof preferences === 'object' ? preferences : {};
  const byShop = root[DISCUSS_AUTO_RULES_PREF_KEY];
  const list = byShop && typeof byShop === 'object'
    ? (Array.isArray(byShop[sid]) ? byShop[sid] : [])
    : [];
  return list.map(normalizeDiscussAutoRule).filter(Boolean);
}

export function buildDiscussAutoRulesPreferencePatch(shopId, rules) {
  const sid = String(shopId || '').trim();
  const normalized = (Array.isArray(rules) ? rules : [])
    .map(normalizeDiscussAutoRule)
    .filter(Boolean);
  return {
    [DISCUSS_AUTO_RULES_PREF_KEY]: {
      [sid]: normalized,
    },
  };
}

/**
 * 平台建议价相对商家最新报价的比例（%）。
 * 例：报价 20、建议 10 → 50。
 */
export function discussSuggestRatioPercent(sku) {
  const suggest = Number(sku?.suggestCostPrice);
  const last = latestCostHistory(sku);
  const quote = Number(
    last?.costPrice != null && last.costPrice !== ''
      ? last.costPrice
      : sku?.latestCostPrice,
  );
  if (!Number.isFinite(suggest) || !Number.isFinite(quote) || quote <= 0) {
    return null;
  }
  return (suggest / quote) * 100;
}

/** Shein 列表行：最后一个 SKU 的比例 */
export function discussRowSuggestRatioPercent(row) {
  const skus = Array.isArray(row?.skuCostPrices) ? row.skuCostPrices : [];
  if (skus.length) return discussSuggestRatioPercent(skus[skus.length - 1]);
  return discussTemuRowSuggestRatioPercent(row);
}

/** Temu 核价单：建议价 ÷ 申报价（%） */
export function discussTemuRowSuggestRatioPercent(row) {
  const suggest = Number(row?.suggestSupplyPrice ?? row?.suggestPrice);
  const declare = Number(row?.supplyPrice ?? row?.declarePrice);
  if (!Number.isFinite(suggest) || !Number.isFinite(declare) || declare <= 0) {
    return null;
  }
  return (suggest / declare) * 100;
}

/** Temu/通用：建议价（元） */
export function discussRowSuggestPriceYuan(row) {
  const fromTemu = discussAmountToYuan(row?.suggestSupplyPrice ?? row?.suggestPrice);
  if (fromTemu != null) return fromTemu;
  const skus = Array.isArray(row?.skuCostPrices) ? row.skuCostPrices : [];
  if (!skus.length) return null;
  const last = skus[skus.length - 1];
  const n = Number(last?.suggestCostPrice);
  return Number.isFinite(n) ? n : null;
}

/**
 * @returns {'accept'|'reject'|null}
 * 比例模式：低于 rejectBelow% → reject；不低于 acceptAbove% → accept。
 * 价格模式：建议价（元）低于 rejectBelowPrice → reject；不低于 acceptAbovePrice → accept。
 */
export function evaluateDiscussAutoAction(row, rules, categoryId = '') {
  const list = (Array.isArray(rules) ? rules : []).filter((r) => r && r.enabled !== false);
  if (!list.length) return null;

  const cat = String(categoryId || row?.categoryId || row?.category_id || '').trim();
  const matched = list.filter((r) => {
    const rid = String(r.categoryId || '').trim();
    if (rid === '*' || rid === 'all') return true;
    return cat && rid === cat;
  });
  if (!matched.length) return null;

  let shouldReject = false;
  let shouldAccept = false;
  for (const rule of matched) {
    const mode = rule.mode === DISCUSS_AUTO_RULE_MODE_PRICE
      ? DISCUSS_AUTO_RULE_MODE_PRICE
      : DISCUSS_AUTO_RULE_MODE_RATIO;
    if (mode === DISCUSS_AUTO_RULE_MODE_PRICE) {
      const yuan = discussRowSuggestPriceYuan(row);
      if (yuan == null) continue;
      if (yuan < Number(rule.rejectBelowPrice)) shouldReject = true;
      if (yuan >= Number(rule.acceptAbovePrice)) shouldAccept = true;
    } else {
      const ratio = discussRowSuggestRatioPercent(row);
      if (ratio == null) continue;
      if (ratio < Number(rule.rejectBelowPercent)) shouldReject = true;
      if (ratio >= Number(rule.acceptAbovePercent)) shouldAccept = true;
    }
  }
  if (shouldReject && shouldAccept) {
    return 'reject';
  }
  if (shouldReject) return 'reject';
  if (shouldAccept) return 'accept';
  return null;
}
